import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/primitives/dialog";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";
import { Switch } from "@/ui/components/primitives/switch";
import { Textarea } from "@/ui/components/primitives/textarea";
import {
  createPSPConfiguration,
  fetchPSPProviders,
  type CreatePSPConfigRequest,
} from "@/services/api/services/psp-management";
import { showErrorToast, showSuccessToast } from "@/services/toast-helper";
import { Loader2 } from "lucide-react";
import { cn } from "@/ui/components/primitives/utils";

interface CreatePSPConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateComplete?: () => void;
}

export function CreatePSPConfigModal({
  open,
  onOpenChange,
  onCreateComplete,
}: CreatePSPConfigModalProps) {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<string[]>([]);

  // Form state
  const [merchantId, setMerchantId] = useState("");
  const [provider, setProvider] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSandbox, setIsSandbox] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState(1);

  // Credentials
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  // URLs
  const [webhookUrl, setWebhookUrl] = useState("");
  const [returnUrl, setReturnUrl] = useState("");

  // Methods and currencies
  const [paymentMethods, setPaymentMethods] = useState("UPI,CARD");
  const [currencies, setCurrencies] = useState("INR,USD");

  // Limits
  const [minAmount, setMinAmount] = useState("100");
  const [maxAmount, setMaxAmount] = useState("100000");

  // Fees
  const [feePercentage, setFeePercentage] = useState("2.5");
  const [feeFixed, setFeeFixed] = useState("0");
  const [feeCurrency, setFeeCurrency] = useState("INR");

  // Features
  const [featuresJson, setFeaturesJson] = useState("{}");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load providers
  useEffect(() => {
    async function loadProviders() {
      try {
        const res = await fetchPSPProviders({ limit: 100 });
        const providerCodes = res.data.map((p) => p.code);
        setProviders(providerCodes);
      } catch (error) {
        console.error("Failed to load providers:", error);
        // Fallback to common providers
        setProviders(["WPAY", "LAFFDA", "funpay", "cryptopsp"]);
      }
    }
    if (open) {
      loadProviders();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setMerchantId("");
    setProvider("");
    setIsEnabled(true);
    setIsSandbox(true);
    setIsActive(true);
    setPriority(1);
    setApiKey("");
    setApiSecret("");
    setBaseUrl("");
    setWebhookUrl("");
    setReturnUrl("");
    setPaymentMethods("UPI,CARD");
    setCurrencies("INR,USD");
    setMinAmount("100");
    setMaxAmount("100000");
    setFeePercentage("2.5");
    setFeeFixed("0");
    setFeeCurrency("INR");
    setFeaturesJson("{}");
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!merchantId.trim()) {
      newErrors.merchantId = "Merchant ID is required";
    }

    if (!provider) {
      newErrors.provider = "Provider is required";
    }

    if (!paymentMethods.trim()) {
      newErrors.paymentMethods = "At least one payment method is required";
    }

    if (!currencies.trim()) {
      newErrors.currencies = "At least one currency is required";
    }

    const min = parseFloat(minAmount);
    const max = parseFloat(maxAmount);
    if (isNaN(min) || min < 0) {
      newErrors.minAmount = "Invalid minimum amount";
    }
    if (isNaN(max) || max < 0) {
      newErrors.maxAmount = "Invalid maximum amount";
    }
    if (!isNaN(min) && !isNaN(max) && max < min) {
      newErrors.maxAmount = "Maximum amount must be greater than minimum";
    }

    const percentage = parseFloat(feePercentage);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      newErrors.feePercentage = "Fee percentage must be between 0 and 100";
    }

    const fixed = parseFloat(feeFixed);
    if (isNaN(fixed) || fixed < 0) {
      newErrors.feeFixed = "Fixed fee must be 0 or greater";
    }

    if (featuresJson.trim()) {
      try {
        JSON.parse(featuresJson);
      } catch {
        newErrors.featuresJson = "Invalid JSON format";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      showErrorToast("Please fix validation errors");
      return;
    }

    setLoading(true);
    try {
      const credentials: Record<string, any> = {};
      if (apiKey) credentials.apiKey = apiKey;
      if (apiSecret) credentials.apiSecret = apiSecret;
      if (baseUrl) credentials.baseUrl = baseUrl;

      const features = featuresJson.trim()
        ? JSON.parse(featuresJson)
        : {};

      const data: CreatePSPConfigRequest = {
        merchantId,
        provider,
        isEnabled,
        isSandbox,
        isActive,
        credentials,
        webhookUrl: webhookUrl || undefined,
        returnUrl: returnUrl || undefined,
        features,
        paymentMethods: paymentMethods.split(",").map((m) => m.trim()),
        currencies: currencies.split(",").map((c) => c.trim()),
        limits: {
          minAmount: parseFloat(minAmount),
          maxAmount: parseFloat(maxAmount),
        },
        fees: {
          percentage: parseFloat(feePercentage),
          fixed: parseFloat(feeFixed),
          currency: feeCurrency,
        },
        priority,
      };

      await createPSPConfiguration(data);
      showSuccessToast("PSP configuration created successfully");
      onOpenChange(false);
      onCreateComplete?.();
    } catch (error: any) {
      showErrorToast(
        error?.detail?.error?.message ||
          error?.message ||
          "Failed to create PSP configuration"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create PSP Configuration</DialogTitle>
          <DialogDescription>
            Configure a new PSP for a merchant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="merchantId">Merchant ID *</Label>
                <Input
                  id="merchantId"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  placeholder="Enter merchant ID"
                  className={cn(errors.merchantId && "border-destructive")}
                />
                {errors.merchantId && (
                  <p className="text-xs text-destructive">{errors.merchantId}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider">PSP Provider *</Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger className={cn(errors.provider && "border-destructive")}>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.provider && (
                  <p className="text-xs text-destructive">{errors.provider}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isEnabled"
                  checked={isEnabled}
                  onCheckedChange={setIsEnabled}
                />
                <Label htmlFor="isEnabled">Enabled</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isSandbox"
                  checked={isSandbox}
                  onCheckedChange={setIsSandbox}
                />
                <Label htmlFor="isSandbox">Sandbox Mode</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority (1-100)</Label>
              <Input
                id="priority"
                type="number"
                min="1"
                max="100"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          {/* Credentials */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Credentials</h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="baseUrl">Base URL</Label>
                <Input
                  id="baseUrl"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.provider.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter API key"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiSecret">API Secret</Label>
                <Input
                  id="apiSecret"
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter API secret"
                />
              </div>
            </div>
          </div>

          {/* URLs */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Webhook & Return URLs</h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://api.paygate.sbs/api/v1/webhooks/provider"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="returnUrl">Return URL</Label>
                <Input
                  id="returnUrl"
                  value={returnUrl}
                  onChange={(e) => setReturnUrl(e.target.value)}
                  placeholder="https://merchant.paygate.sbs/payment/success"
                />
              </div>
            </div>
          </div>

          {/* Payment Methods & Currencies */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Payment Methods & Currencies</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentMethods">Payment Methods (comma-separated) *</Label>
                <Input
                  id="paymentMethods"
                  value={paymentMethods}
                  onChange={(e) => setPaymentMethods(e.target.value)}
                  placeholder="UPI,CARD,NET_BANKING"
                  className={cn(errors.paymentMethods && "border-destructive")}
                />
                {errors.paymentMethods && (
                  <p className="text-xs text-destructive">{errors.paymentMethods}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="currencies">Currencies (comma-separated) *</Label>
                <Input
                  id="currencies"
                  value={currencies}
                  onChange={(e) => setCurrencies(e.target.value)}
                  placeholder="INR,USD"
                  className={cn(errors.currencies && "border-destructive")}
                />
                {errors.currencies && (
                  <p className="text-xs text-destructive">{errors.currencies}</p>
                )}
              </div>
            </div>
          </div>

          {/* Limits */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Transaction Limits</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minAmount">Minimum Amount *</Label>
                <Input
                  id="minAmount"
                  type="number"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder="100"
                  className={cn(errors.minAmount && "border-destructive")}
                />
                {errors.minAmount && (
                  <p className="text-xs text-destructive">{errors.minAmount}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxAmount">Maximum Amount *</Label>
                <Input
                  id="maxAmount"
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder="100000"
                  className={cn(errors.maxAmount && "border-destructive")}
                />
                {errors.maxAmount && (
                  <p className="text-xs text-destructive">{errors.maxAmount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Fees */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Fees</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="feePercentage">Percentage Fee (%) *</Label>
                <Input
                  id="feePercentage"
                  type="number"
                  step="0.1"
                  value={feePercentage}
                  onChange={(e) => setFeePercentage(e.target.value)}
                  placeholder="2.5"
                  className={cn(errors.feePercentage && "border-destructive")}
                />
                {errors.feePercentage && (
                  <p className="text-xs text-destructive">{errors.feePercentage}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="feeFixed">Fixed Fee *</Label>
                <Input
                  id="feeFixed"
                  type="number"
                  value={feeFixed}
                  onChange={(e) => setFeeFixed(e.target.value)}
                  placeholder="0"
                  className={cn(errors.feeFixed && "border-destructive")}
                />
                {errors.feeFixed && (
                  <p className="text-xs text-destructive">{errors.feeFixed}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="feeCurrency">Fee Currency *</Label>
                <Input
                  id="feeCurrency"
                  value={feeCurrency}
                  onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())}
                  placeholder="INR"
                  maxLength={3}
                />
              </div>
            </div>
          </div>

          {/* Features (JSON) */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Features (JSON)</h3>

            <div className="space-y-2">
              <Label htmlFor="featuresJson">Features Configuration</Label>
              <Textarea
                id="featuresJson"
                value={featuresJson}
                onChange={(e) => setFeaturesJson(e.target.value)}
                placeholder='{"refunds": true, "recurring": false}'
                rows={3}
                className={cn(errors.featuresJson && "border-destructive")}
              />
              {errors.featuresJson && (
                <p className="text-xs text-destructive">{errors.featuresJson}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
